import { axiosSecuredInstance } from "configurations/axiosConfig";
import * as ApiModel from "entities/ApiModel";
import {
  ActionResult,
  Agency,
  Jacket,
  Pricing,
  PricingProduct,
  SCFile,
} from "entities/UIModel";
import {
  createHook,
  createStore,
  StoreActionApi,
} from "react-sweet-state";
import {
  ActionResultStatus,
  DOMEventType,
  ProductAction,
  ProductType,
} from "utils/data/enum";
import {
  getFormikAals,
  getFormikAdditionalParties,
  getFormikCpls,
  getFormikInitialValues,
  getFormikJackets,
  getFormikStandaloneEndorsements,
} from "utils/data/formikInitialValues";
import {
  hasErrorOnAllProductsAction,
  hasErrorOnSingleProductAction,
} from "pages/file/utils/helper";
import { setSCFile } from "pages/file/utils/fileHelper";
import { convertSQLDBNullToUndefined } from "../shared";

interface State {
  initialValues: SCFile;
  isFileLoading: boolean;
  isSaving: boolean;
  isActionInProgress: boolean;
  error: any;
  uniqueFileId: number;
  isRevising: boolean; // should be called isRevising, during revise it's set to true
  calculatedPricingProducts: PricingProduct[] | null;
  isReadOnly: boolean;
  isReadOnlyMode ?: boolean;
  invokingUrl: string;
  invokingAction: string;
}

type StoreApi = StoreActionApi<State>;

const setLoading =
  () =>
  ({ setState }: StoreApi) => {
    setState({ error: null, isFileLoading: true });
  };

const setSaving =
  () =>
  ({ setState }: StoreApi) => {
    setState({ isSaving: true });
  };

const setError =
  (error: any) =>
  ({ setState }: StoreApi) => {
    setState({
      error,
      isSaving: false,
      isFileLoading: false,
    });
  };

const setFile =
  (
    fileFromMiddleware: ApiModel.SCFile,
    isPopulatingExistingFile: boolean = false,
    hasError: boolean = false,
    resetInitialValues: boolean = true
  ) =>
  ({ setState }: StoreApi) => {
    const initialValuesFromApi = setSCFile(
      fileFromMiddleware,
      isPopulatingExistingFile
    );
    setState({
      isFileLoading: false,
      isReadOnly: initialValuesFromApi.isReadOnly,
      isReadOnlyMode :initialValuesFromApi.isReadOnly,
      // isPricingRecalculateRequired: false,
    });

    if (!hasError) {
      if (resetInitialValues) {
        setState({
          initialValues: initialValuesFromApi,
          calculatedPricingProducts: null,
        });

        document.dispatchEvent(new Event(DOMEventType.FILE_JUST_LOADED));
      }
    }

    return initialValuesFromApi;
  };

const Store = createStore<State, any>({
  initialState: {
    initialValues: getFormikInitialValues(),
    isFileLoading: false,
    isSaving: false,
    isActionInProgress: false,
    error: null,
    uniqueFileId: 0,
    isRevising: false,
    // isPricingRecalculateRequired: false,
    // pricingNotificationUpdated: false,
    calculatedPricingProducts: null,
    isReadOnly: false,
    isReadOnlyMode :false,
    invokingUrl: "/",
    invokingAction: "",
  },
  actions: {
    setErrorFiles:
      (error: any) =>
      ({ dispatch }: StoreApi) => {
        dispatch(setError(error));
      },
    setCalculatedPricingProducts:
      (value: PricingProduct[] | null) =>
      ({ setState }: StoreApi) => {
        setState({ calculatedPricingProducts: value });
      },
    setFileLoading:
      (isFileLoading: boolean) =>
      async ({ setState }: StoreApi) => {
        setState({ isFileLoading: isFileLoading });
      },
    saveFile:
      (scfile: ApiModel.SCFile, isCleaningForm: boolean) =>
      async ({ setState, dispatch }: StoreApi): Promise<ActionResult> => {
        try {
          let savedScfile: SCFile | undefined;
          setState({ error: null });
          dispatch(setSaving());
          const { data, headers } = await axiosSecuredInstance.put(
            "/files/savefile",
            scfile
          );
          if (!isCleaningForm) {
            savedScfile = dispatch(setFile(data));
          }
          setState({ isSaving: false });
          return {
            headers: headers,
            apiResponse: data,
            scfile: savedScfile,
          };
        } catch (error) {
          dispatch(setError(error));
          setState({ isSaving: false });
          // robun's notes: for some reason the dispatch(setError) wont's finish udpating context soon enough
          return {
            error: error,
          };
        }
      },
    getFileById:
      (fileId: number | string) =>
      async ({ dispatch }: StoreApi) => {
        try {
          dispatch(setLoading());
          const { data } = await axiosSecuredInstance.get(`/files/${fileId}`);
          return dispatch(setFile(data, true));
        } catch (error) {
          dispatch(setError(error));
          return error;
        }
      },
    getFileActivities:
    (fileId: number | string) =>
      async ({ dispatch }: StoreApi): Promise<ActionResult> => {
        const result:ActionResult = {};
        try {
          const { data } = await axiosSecuredInstance.get(`/files/GetFileActivities/${fileId}`);
          result.apiResponse = data;
          result.status = ActionResultStatus.Success;
          return result;
        } catch (error) {
          dispatch(setError(error));
          result.error = error;
          result.status = ActionResultStatus.Failed;
          return result;
        }
      },    
    deleteProperty:
      (fileId: number, propertyId: number) =>
      async ({ dispatch }: StoreApi) => {
        try {
          await axiosSecuredInstance.delete(
            `/files/fileproperty?fileId=${fileId}&propertyId=${propertyId}`
          );
        } catch (error) {
          dispatch(setError(error));
          return error;
        }
      },
    deleteOrder:
      (orderId: number) =>
      async ({ dispatch }: StoreApi) => {
        try {
          await axiosSecuredInstance.delete(`/order/DeleteOrder/${orderId}`);
        } catch (error) {
          dispatch(setError(error));
          return error;
        }
      },
    deleteFileData:
      (deleteCriteria: ApiModel.DeleteCriteria) =>
      async ({ dispatch }: StoreApi) => {
        try {
          await axiosSecuredInstance.post(`/files/filedata`, deleteCriteria);
        } catch (error) {
          dispatch(setError(error));
          return error;
        }
      },
    submitTitleSearch:
      (scfile: ApiModel.SCFile, requestId: string,  productAction: string, integrationKey: string) =>
      async ({
        dispatch,
        setState,
        getState,
      }: StoreApi): Promise<ActionResult> => {
        try {
          setState({ isActionInProgress: true, error: null });
          const { data, headers } = await axiosSecuredInstance.post(
            `/order/product?ProductType=${ProductType.TitleSearch}&ProductAction=${productAction}&requestId=${requestId}`,
            scfile
          );
          const currentFile = dispatch(setFile(data));

          // Only check for product error message for Issue and IssueResent
          let hasError = false;
          if ([ProductAction.Issue.toString(), ProductAction.IssueResend.toString()].includes(productAction)) {
            hasError = hasErrorOnSingleProductAction(
              data,
              productAction,
              ProductType.TitleSearch,
              integrationKey,
            );
          }

          setState({ isActionInProgress: false });

          return {
            status: hasError ? ActionResultStatus.Failed : ActionResultStatus.Success,
            headers: headers,
            scfile: currentFile,
          };
        } catch (error) {
          dispatch(setError(error));
          setState({ isActionInProgress: false });
          return {
            status: ActionResultStatus.Failed,
            scfile: getState().initialValues,
            error,
          };
        }
      },      
    submitUwa:
      (scfile: ApiModel.SCFile, requestId: string,  productAction: string, integrationKey: string) =>
      async ({
        dispatch,
        setState,
        getState,
      }: StoreApi): Promise<ActionResult> => {
        try {
          setState({ isActionInProgress: true, error: null });
          const { data, headers } = await axiosSecuredInstance.post(
            `/order/product?ProductType=${ProductType.UWARequest}&ProductAction=${productAction}&requestId=${requestId}`,
            scfile
          );
          const currentFile = dispatch(setFile(data));

          // Only check for product error message for Issue and IssueResent
          let hasError = false;
          if ([ProductAction.Issue.toString()].includes(productAction)) {
            hasError = hasErrorOnSingleProductAction(
              data,
              productAction,
              ProductType.UWARequest,
              integrationKey,
            );
          }

          setState({ isActionInProgress: false });

          return {
            status: hasError ? ActionResultStatus.Failed : ActionResultStatus.Success,
            headers: headers,
            scfile: currentFile,
          };
        } catch (error) {
          dispatch(setError(error));
          setState({ isActionInProgress: false });
          return {
            status: ActionResultStatus.Failed,
            scfile: getState().initialValues,
            error,
          };
        }
      },            
    issueCpl:
      (scfile: ApiModel.SCFile, requestId: string) =>
      async ({
        dispatch,
        setState,
        getState,
      }: StoreApi): Promise<ActionResult> => {
        try {
          setState({ isActionInProgress: true, error: null });
          const { data, headers } = await axiosSecuredInstance.post(
            `/order/product?ProductType=${ProductType.Cpl}&ProductAction=${ProductAction.Issue}&requestId=${requestId}`,
            scfile
          );
          const currentFile = dispatch(setFile(data));

          setState({ isActionInProgress: false });

          return {
            headers: headers,
            scfile: currentFile,
          };
        } catch (error) {
          dispatch(setError(error));
          setState({ isActionInProgress: false });
          return {
            scfile: getState().initialValues,
            error,
          };
        }
      },
    issueAAL:
      (scfile: ApiModel.SCFile, requestId: string) =>
      async ({
        dispatch,
        setState,
        getState,
      }: StoreApi): Promise<ActionResult> => {
        try {
          setState({ isActionInProgress: true, error: null });
          const { data, headers } = await axiosSecuredInstance.post(
            `/order/product?ProductType=${ProductType.Aal}&ProductAction=${ProductAction.Issue}&requestId=${requestId}`,
            scfile
          );
          const currentFile = dispatch(setFile(data));

          setState({ isActionInProgress: false });

          return {
            headers: headers,
            scfile: currentFile,
          };
        } catch (error) {
          dispatch(setError(error));
          setState({ isActionInProgress: false });
          return {
            scfile: getState().initialValues,
            error,
          };
        }
      },
    voidProduct:
      (
        scfile: ApiModel.SCFile,
        requestId: string,
        productType: string,
        integrationKey: string,
        parentProductType: string | null,
        parentOrderID?: number
      ) =>
      async ({ dispatch, getState }: StoreApi): Promise<ActionResult> => {
        try {
          if (!parentOrderID) parentOrderID = 0;
          //if(!parentProductType) parentProductType = null;
          let voidUrl = `/order/product?ProductType=${productType}&ProductAction=${ProductAction.Void}&requestId=${requestId}`;
          if (parentProductType) {
            voidUrl =
              voidUrl +
              `&parentProductType=${parentProductType}&parentOrderID=${parentOrderID}`;
          }
          const { data, headers } = await axiosSecuredInstance.post(voidUrl, scfile);
          const hasError = hasErrorOnSingleProductAction(
            data,
            ProductAction.Void,
            productType,
            integrationKey,
            parentProductType || "",
            parentOrderID
          );
          const currentFile = dispatch(setFile(data, false, hasError));

          return {
            headers: headers,
            scfile: currentFile,
          };
        } catch (error) {
          dispatch(setError(error));

          return {
            scfile: getState().initialValues,
            error,
          };
        }
      },
    undoVoidProduct:
      (
        scfile: ApiModel.SCFile,
        requestId: string,
        productType: string,
        integrationKey: string,
      ) =>
      async ({ dispatch, getState }: StoreApi): Promise<ActionResult> => {
        try {
          let undoVoidUrl = `/order/product?ProductType=${productType}&ProductAction=${ProductAction.UndoVoid}&requestId=${requestId}`;
          const { data, headers } = await axiosSecuredInstance.post(undoVoidUrl, scfile);
          const hasError = hasErrorOnSingleProductAction(
            data,
            ProductAction.Void,
            productType,
            integrationKey,
            undefined, // parentProductType || "",
            undefined // parentOrderID
          );
          console.log("hasError:", hasError);
          const currentFile = dispatch(setFile(data, false, hasError));

          return {
            headers: headers,
            scfile: currentFile,
            status: hasError ? ActionResultStatus.Failed : ActionResultStatus.Success,
          };
        } catch (error) {
          console.log(error);
          dispatch(setError(error));

          return {
            scfile: getState().initialValues,
            error,
            status: ActionResultStatus.Failed,
          };
        }
      },      
    voidAllProducts:
      (
        scfile: ApiModel.SCFile,
        requestId: string,
        productType: string,
        integrationKeys: string[]
      ) =>
      async ({ dispatch, getState }: StoreApi): Promise<ActionResult> => {
        try {
          dispatch(setLoading());
          let voidUrl = `/order/product?ProductType=${productType}&ProductAction=${ProductAction.Void}&requestId=${requestId}`;
          const { data, headers } = await axiosSecuredInstance.post(voidUrl, scfile);
          const hasError = hasErrorOnAllProductsAction(data, integrationKeys);
          const currentFile = dispatch(setFile(data, false, hasError));

          return {
            headers: headers,
            scfile: currentFile,
          };
        } catch (error) {
          dispatch(setError(error));

          return {
            scfile: getState().initialValues,
            error,
          };
        }
      },
    issueJacket:
      (scfile: ApiModel.SCFile, requestId: string) =>
      async ({
        setState,
        getState,
        dispatch,
      }: StoreApi): Promise<ActionResult> => {
        try {
          setState({
            isActionInProgress: true,
            error: null,
          });
          const { data, headers } = await axiosSecuredInstance.post(
            `/order/product?ProductType=${ProductType.Jacket}&ProductAction=${ProductAction.Issue}&requestId=${requestId}`,
            scfile
          );
          const currentFile = dispatch(setFile(data));

          setState({ isActionInProgress: false });

          return {
            headers: headers,
            scfile: currentFile,
          };
        } catch (error) {
          dispatch(setError(error));
          setState({ isActionInProgress: false });

          return {
            scfile: getState().initialValues,
            error,
          };
        }
      },
    revise:
      (
        scfile: ApiModel.SCFile,
        productType: ProductType,
        requestId: string,
        integrationKey: string
      ) =>
      async ({
        dispatch,
        setState,
        getState,
      }: StoreApi): Promise<ActionResult> => {
        try {
          dispatch(setLoading());
          const { data, headers } = await axiosSecuredInstance.post(
            `/order/product?ProductType=${productType}&ProductAction=${ProductAction.Revise}&requestId=${requestId}`,
            scfile
          );

          const currentFile = dispatch(setFile(data, false));
          setState({ isActionInProgress: false });
          return {
            headers: headers,
            scfile: currentFile,
          };
        } catch (error) {
          dispatch(setError(error));
          setState({ isActionInProgress: false });
          return {
            scfile: getState().initialValues,
            error,
          };
        }
      },
    submitEndorsements:
      (scfile: SCFile, requestId: string, orderId?: number) =>
      async ({ dispatch, getState }: StoreApi): Promise<ActionResult> => {
        try {
          dispatch(setLoading());

          const { data, headers } = await axiosSecuredInstance.post(
            `/order/product?ProductType=${ProductType.Endorsement}&ProductAction=${ProductAction.Issue}&requestId=${requestId}&parentProductType=${ProductType.Jacket}&parentOrderId=${orderId}`,
            scfile
          );

          const currentFile = dispatch(setFile(data));
          return {
            headers: headers,
            scfile: currentFile,
          };
        } catch (error) {
          dispatch(setError(error));
          return {
            scfile: getState().initialValues,
            error,
          };
        }
      },
    submitStandaloneEndorsement:
      (
        scfile: ApiModel.SCFile,
        productType: string,
        requestId: string,
        orderId?: number
      ) =>
      async ({
        dispatch,
        getState,
        setState,
      }: StoreApi): Promise<ActionResult> => {
        try {
          dispatch(setLoading());
          const { data, headers } = await axiosSecuredInstance.post(
            `/order/product?ProductType=${productType}&ProductAction=${ProductAction.Issue}&requestId=${requestId}&parentProductType=${ProductType.StandaloneEndorsement}&parentOrderId=${orderId}`,
            scfile
          );

          const currentFile = dispatch(setFile(data));
          setState({ isActionInProgress: false });
          return {
            headers: headers,
            scfile: currentFile,
          };
        } catch (error) {
          dispatch(setError(error));
          return {
            scfile: getState().initialValues,
            error,
          };
        }
      },
    deleteEndorsement:
      (endorsementID: number) =>
      async ({ dispatch }: StoreApi) => {
        try {
          await axiosSecuredInstance.delete(
            `/order/Endorsement?endorsementId=${endorsementID}`
          );
        } catch (error) {
          dispatch(setError(error));
          return error;
        }
      },
    resetInitialValues:
      () =>
      ({ setState }: StoreApi) => {
        const initialValues = getFormikInitialValues();
        setState({
          initialValues: initialValues,
          calculatedPricingProducts: null,
          isReadOnly: false,
          isReadOnlyMode :false,
        });
      },
    resetNonFileSections:
      (
        fileId: number,
        fileNumber: string,
        agency: Agency,
        isFromModal: boolean
      ) =>
      async ({ setState, getState }: StoreApi) => {
        const initialValues = getState().initialValues;

        initialValues.id = fileId;
        initialValues.agency = agency;

        if (fileId === 0) initialValues.identityKey = 0;

        if (fileNumber && fileNumber !== "")
          initialValues.fileNameNumber = fileNumber;

        setState({
          initialValues: initialValues,
        });
      },
    resetProductAndAdditionalParty:
      (currentScFile: SCFile) =>
      async ({ setState }: StoreApi) => {
        const newInitialValues = currentScFile;

        // update product and additional party sections
        newInitialValues.additionalParties = getFormikAdditionalParties();
        newInitialValues.jackets = getFormikJackets();
        newInitialValues.cpls = getFormikCpls();
        newInitialValues.aALProducts = getFormikAals();
        newInitialValues.standaloneEndorsements =
          getFormikStandaloneEndorsements();

        // update initialValues
        setState({
          initialValues: newInitialValues,
        });
      },
    setRevising:
      (isRevising: boolean) =>
      ({ setState }: StoreApi) => {
        setState({ isRevising: isRevising });
      },
    setInvokingUrl:
      (url: string, action: string) =>
      ({ setState }: StoreApi) => {
        setState(
          { 
            invokingUrl: url,
            invokingAction: action
          });
      },
    resetInvokingUrl:
      () =>
      ({ setState }: StoreApi) => {
        setState(
          { 
            invokingUrl: "",
            invokingAction: ""
          });
      },  
    setIsReadOnly:
    (readOnly: boolean) =>
    ({ setState }: StoreApi) => {
      setState(
        { 
          isReadOnly: readOnly
        });
    },
    setIsReadOnlyMode:
    (readOnly: boolean) =>
    ({ setState }: StoreApi) => {
      setState(
        { 
          isReadOnlyMode: readOnly
        });
    },
    resetPricingFields: (fields: Partial<Pricing>) => ({ setState, getState }: StoreApi) => {
      const initialValues = getState().initialValues;

      initialValues.pricing = { ...initialValues.pricing, ...fields };

      setState({ initialValues })
    },
    resetJacketPricingDetail: (currentJacket: Jacket) => ({ setState, getState }: StoreApi) => {
      const initialValues = getState().initialValues;

      const filePricingDetailID = currentJacket.pricingDetail?.filePricingDetailID

      const index = initialValues.jackets?.findIndex(j => j.filePricingDetailID === filePricingDetailID);

      if (index >= 0) {
        let jacket = initialValues.jackets[index];
        if (jacket.pricingDetail) {
          jacket.pricingDetail.isReissue = currentJacket?.pricingDetail?.isReissue;
          jacket.originalJacketDate = new Date(currentJacket.originalJacketDate ?? 0);
          jacket.originalJacketLiability = convertSQLDBNullToUndefined(currentJacket.originalJacketLiability);
          jacket.originalJacketNumber = currentJacket.originalJacketNumber;
        }
      }

      setState({ initialValues });
    },
  },
  name: "files",
});

const useInitialValuesFiles = createHook(Store, {
  selector: (state: State) => ({ initialValues: state.initialValues }),
});

const useInitialValuesSingleProp = createHook(Store, {
  selector: (state: State, arg: keyof SCFile) => ({
    initialValuesProp: state.initialValues[arg],
  }),
});

const useIsFileLoadingFiles = createHook(Store, {
  selector: (state: State) => ({ isFileLoading: state.isFileLoading }),
});

const useIsActionInProgressFiles = createHook(Store, {
  selector: (state: State) => ({
    isActionInProgress: state.isActionInProgress,
  }),
});

const useFilesActions = createHook(Store, { selector: null });

const hook = createHook(Store);
const useFiles = () => {
  return hook();
};

export {
  useInitialValuesFiles,
  useIsFileLoadingFiles,
  useIsActionInProgressFiles,
  useFiles,
  useInitialValuesSingleProp,
  useFilesActions,
  Store,
};
